import { ChangeDetectorRef, Component, NgZone } from '@angular/core';
import { Platform, NavController } from '@ionic/angular';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { GlobalMethodsService } from './services/global-methods.service';
import { NavigationService } from './services/commons/navigation.service';
import { GlobalVariablesService } from './services/global-variables.service';
import { Installation } from './models/Installation';
import { FcmService } from './services/commons/fcm.service';
import { Router } from '@angular/router';
import { TranslateConfigService } from './services/commons/translate-config.service';
import { LocationService } from './services/Location/location.service';
import { StorageService } from './services/commons/storage.service';
import { environment } from '../environments/environment.pre';
import { App } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { EventNotificationService } from './services/commons/event-notification.service';
import * as moment from 'moment';
import { ModalService } from './services/commons/modal.service';
import { NewsService } from './services/commons/news.service';
import { VaelsysWebrtcService } from './services/vaelsys/vaelsys-webrtc.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent {

  public alarmActivated: boolean = false; // Home alarm activated -> alarmActivated = true | Home default -> alarmActivated = false
  public timerPanelActivated: boolean = false; // Timer is running -> timerPanelActivated = true | Home default -> timerPanelActivated = false
  public newFeaturesAvailable: boolean = false; // There are news to show -> newFeaturesAvailable = true | Home default -> newFeaturesAvailable = false
  public showHeader: boolean = true;
  public showFooter: boolean = true;
  public user: string;
  public customBackground: boolean = false; // so that the background of the map appears on the page (geocerca / geo-around)

  constructor(
    private platform: Platform,
    public gms: GlobalMethodsService,
    private zone: NgZone,
    private navigation: NavigationService,
    public gvs: GlobalVariablesService,
    private navCtrl: NavController,
    private fcm: FcmService,
    private router: Router,
    public locationService: LocationService,
    private storage: StorageService,
    private eventNotification: EventNotificationService,
    private modalService: ModalService,
    public newsService: NewsService,
    private cd: ChangeDetectorRef,
    private vaelsysWebrtcService: VaelsysWebrtcService
  ) {

    if (this.platform.is('desktop') || this.platform.is('mobileweb')) {
      FirebaseAnalytics.initializeFirebase(environment.FIREBASE_CONFIG);
    }

    this.gms.setupPageEvent().subscribe((setupPage) => {
      this.zone.run(async () => {
        this.showHeader = setupPage.showHeader;
        this.showFooter = setupPage.showFooter;
      });
    });

    this.gms.statusAlarmEvent().subscribe((status) => {
      this.zone.run(async () => {
        this.alarmActivated = status;
      });
    });

    this.gms.statusTimerPanelEvent().subscribe((status) => {
      this.zone.run(async () => {
        this.timerPanelActivated = status;
        this.cd.detectChanges();
      });
    });

    this.gms.statusNewsPanelEvent().subscribe((status) => {
      this.zone.run(async () => {
        this.newFeaturesAvailable = status;
      });
    });

    this.gms.fullBackgroundEvent().subscribe((status) => {
      this.zone.run(async () => {
        this.customBackground = status;
      });
    });

    this.gms.installationEvent().subscribe(async (installation: Installation) => {
      this.zone.run(async () => {
        if (installation != null) {
          this.user = installation.user_name;
        }
      });
    });

    this.platform.pause.subscribe(async () => {
      this.gvs.sessionStartTime = new Date();
      if (this.router.url.includes('/cameras')) {
        this.vaelsysWebrtcService.updateStatusService$.next({ status: false, user: this.user });
      }
    });

    this.platform.resume.subscribe(async () => {
      let resumeTime = new Date();
      let startTime = this.gvs.sessionStartTime;
      startTime.setHours(startTime.getHours() + 4);
      if (this.router.url.includes('/cameras')) {
        this.gms.setLoadingTrue('', true);
        if (this.modalService.isModalOpen('recordingsModal')) {
          this.modalService.hide('recordingsModal');
        }
        this.navCtrl.navigateRoot('/home', { animated: false });
        await this.gms.sleep(1000);
        this.navCtrl.navigateRoot('/cameras');
        await this.gms.sleep(700);
        this.gms.setLoadingFalse();
      }

      // let time = new Date(this.gvs.sessionStartTime.getTime() + 60000);
      if (resumeTime.getTime() >= startTime.getTime()) {
        zone.run(async () => {
          // await this.gms.clearSession();
          this.modalService.closeAllOpenModals();
          this.navCtrl.navigateRoot('/splash-screen', { animated: false });
        });
      }
    });


    if (this.platform.is('cordova')) {
      SplashScreen.hide();
    }

    this.initializeApp();
  }

  ngOnInit() {
  }

  initializeApp() {
    this.platform.ready().then(async () => {
      let app_version = await this.storage.getObject('APP_VERSION');
      if (app_version == null) {
        await this.storage.setObject('APP_VERSION', environment.APP_VERSION);
      } else {
        let version = environment.APP_VERSION;
        if (app_version !== version) {
          await this.storage.removeObject('APP_VERSION');
          await this.storage.removeObject('FIRST_USE');
          await this.storage.removeObject(this.gvs.EXPIRATION_AUTH);
          await this.storage.removeObject(this.gvs.TOKEN_EXPIRATION_KEY);
          await this.storage.removeObject(this.gvs.TOKEN_EXPIRATION_KEY_PREAUTH);
          await this.storage.removeObject(this.gvs.TOKEN_KEY);
          await this.storage.removeObject(this.gvs.CARDS_NEWS);
          await this.storage.setObject('APP_VERSION', environment.APP_VERSION);
        }
      }
      this.gms.setSetupPage(false, false, false, false);
      if (await this.platform.is('cordova') && await this.platform.is('ios')) {
        Keyboard.setAccessoryBarVisible({ isVisible: true });
      }
      this.platform.backButton.subscribeWithPriority(1, () => {
        if (this.router.url.includes('/login')) {
          navigator['app'].exitApp();
        } else if (this.router.url.includes('/home')) {
          App.exitApp();
          return;
        } else if (this.router.url.includes('/register')) {
          this.navCtrl.navigateRoot('/login', { animated: false });
        } else {
          this.navigation.goBack();
        }
        Promise.reject();
      });
      if (await this.platform.is('cordova')) {
        await this.fcm.registerPush();
        this.eventNotification.subscribeNotifications();
        this.eventNotification.subscribeRedirectNotification();
      }
      //await this.navigation.goTo("/splash-screen");
    });
  }

  async checkNewsInPage() {
    if (!this.router.url.includes('/home') && !this.router.url.includes('/splash-screen') && !this.router.url.includes('/login')) {
      this.newsService.checkCardsNews();
    }
  }

}
